import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeTier, TIER_LEVELS, type SubscriptionTier } from '@/lib/tiers/index';

// Re-export for backwards compatibility
export type { SubscriptionTier } from '@/lib/tiers/index';

interface SubscriptionContextType {
  subscribed: boolean;
  tier: SubscriptionTier | null;
  subscriptionEnd: string | null;
  paymentIssue: boolean;
  monthlyCredits: number;
  dailyBonus: number;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  hasAccess: (requiredTier?: SubscriptionTier | null) => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [subscribed, setSubscribed] = useState(false);
  const [tier, setTier] = useState<SubscriptionTier | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [paymentIssue, setPaymentIssue] = useState(false);
  const [monthlyCredits, setMonthlyCredits] = useState(0);
  const [dailyBonus, setDailyBonus] = useState(1);
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef<number>(0);
  const inflightRef = useRef<Promise<void> | null>(null);

  const loadFromProfile = async () => {
    if (!user) return;
    
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_end')
        .eq('id', user.id)
        .single();

      if (profile) {
        const rawTier = profile.subscription_tier as string | null;
        const normalizedTier = rawTier ? normalizeTier(rawTier) : null;
        setTier(normalizedTier);
        setSubscribed(!!normalizedTier);
        setSubscriptionEnd(profile.subscription_end);
      }
    } catch (err) {
      if (import.meta.env.DEV) console.warn('Failed to load profile:', err);
    }
  };

  const refreshSubscription = useCallback(async () => {
    if (!user) {
      setSubscribed(false);
      setTier(null);
      setSubscriptionEnd(null);
      setPaymentIssue(false);
      setMonthlyCredits(0);
      setDailyBonus(1);
      setLoading(false);
      return;
    }

    // Dedup: skip if fetched < 5s ago
    const now = Date.now();
    if (now - lastFetchRef.current < 5000) {
      setLoading(false);
      return;
    }

    // Dedup: reuse inflight request
    if (inflightRef.current) {
      await inflightRef.current;
      return;
    }

    const doFetch = async () => {
      try {
        lastFetchRef.current = Date.now();
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        
        if (!accessToken) {
          await loadFromProfile();
          return;
        }

        const { data, error } = await supabase.functions.invoke('check-subscription', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (error) {
          if (import.meta.env.DEV) console.warn('Error checking subscription:', error);
          await loadFromProfile();
          return;
        }

        const rawTier = data.tier || null;
        const normalizedTier = rawTier ? normalizeTier(rawTier) : null;
        
        setSubscribed(data.subscribed || false);
        setTier(normalizedTier);
        setSubscriptionEnd(data.subscription_end || null);
        setPaymentIssue(data.payment_issue || false);
        setMonthlyCredits(data.monthly_credits || 0);
        setDailyBonus(data.daily_bonus || 1);
      } catch (err) {
        if (import.meta.env.DEV) console.warn('Failed to check subscription:', err);
        await loadFromProfile();
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    };

    inflightRef.current = doFetch();
    await inflightRef.current;
  }, [user]);

  // Check if user has access to a feature based on tier
  const hasAccess = useCallback((requiredTier?: SubscriptionTier | null): boolean => {
    if (!requiredTier) return true;
    if (!tier) return false;
    
    const userLevel = TIER_LEVELS[tier] || 0;
    const requiredLevel = TIER_LEVELS[requiredTier] || 0;
    
    return userLevel >= requiredLevel;
  }, [tier]);

  useEffect(() => {
    if (user) {
      refreshSubscription();
      const interval = setInterval(refreshSubscription, 60000);
      return () => clearInterval(interval);
    } else {
      setSubscribed(false);
      setTier(null);
      setSubscriptionEnd(null);
      setPaymentIssue(false);
      setMonthlyCredits(0);
      setDailyBonus(1);
      setLoading(false);
    }
  }, [user, refreshSubscription]);

  return (
    <SubscriptionContext.Provider 
      value={{ subscribed, tier, subscriptionEnd, paymentIssue, monthlyCredits, dailyBonus, loading, refreshSubscription, hasAccess }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
